import React, { useState, useEffect } from 'react';
import { UserPlus, Shield, Edit, Trash2, Eye, EyeOff, CheckSquare, Square, KeyRound, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TeamManagement = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [permissions, setPermissions] = useState({ permissions: [], categories: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [sendingReset, setSendingReset] = useState(null);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [changingPasswordFor, setChangingPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'sub_admin',
    permissions: []
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, permsRes] = await Promise.all([
        api.get('/team'),
        api.get('/permissions')
      ]);
      setTeamMembers(membersRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMember) {
        // Update existing member - include email if changed
        const updateData = { 
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          role: formData.role,
          permissions: formData.permissions
        };
        
        // Include email if it has changed
        if (formData.email !== editingMember.email) {
          updateData.email = formData.email;
        }
        
        await api.put(`/team/${editingMember.id}`, updateData);
        toast.success('Team member updated successfully!');
      } else {
        // Create new member
        await api.post('/team', formData);
        toast.success('Team member created successfully!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      email: member.email,
      password: '',
      first_name: member.first_name,
      last_name: member.last_name,
      phone: member.phone || '',
      role: member.role,
      permissions: member.permissions || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      await api.delete(`/team/${memberId}`);
      toast.success('Team member removed successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team member');
    }
  };

  const handleSendPasswordReset = async (member) => {
    setSendingReset(member.id);
    try {
      await api.post(`/team/${member.id}/send-password-reset`);
      toast.success(`Password reset link sent to ${member.email}`);
    } catch (error) {
      console.error('Failed to send password reset:', error);
      toast.error(error.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setSendingReset(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post(`/team/${changingPasswordFor.id}/change-password`, {
        new_password: newPassword
      });
      toast.success(`Password changed for ${changingPasswordFor.email}`);
      setChangePasswordDialogOpen(false);
      setChangingPasswordFor(null);
      setNewPassword('');
      setShowNewPassword(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const openChangePasswordDialog = (member) => {
    setChangingPasswordFor(member);
    setNewPassword('');
    setShowNewPassword(false);
    setChangePasswordDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'sub_admin',
      permissions: []
    });
    setEditingMember(null);
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const handleSelectAllInCategory = (categoryPerms) => {
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="team-title">Team Management</h2>
          <p className="text-gray-600">Manage admins and sub-admins with custom permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-team-member-button"
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    data-testid="member-first-name-input"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    data-testid="member-last-name-input"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="member-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
                {editingMember && (
                  <p className="text-xs text-gray-500 mt-1">
                    Changing the email will update the login credentials
                  </p>
                )}
              </div>

              {!editingMember && (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="member-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  data-testid="member-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Full Admin</SelectItem>
                    <SelectItem value="sub_admin">Sub Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Full admins have all permissions. Sub admins have custom permissions.
                </p>
              </div>

              {formData.role === 'sub_admin' && (
                <div>
                  <Label className="mb-3 block">Permissions</Label>
                  <div className="space-y-4 border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                    {Object.entries(permissions.categories).map(([category, perms]) => (
                      <div key={category} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm text-gray-900">{category}</h4>
                          <button
                            type="button"
                            onClick={() => handleSelectAllInCategory(perms)}
                            className="text-xs text-teal-600 hover:text-teal-700"
                          >
                            {perms.every(p => formData.permissions.includes(p)) ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        <div className="space-y-2 pl-4">
                          {perms.map((perm) => (
                            <div key={perm} className="flex items-center space-x-2">
                              <Checkbox
                                id={perm}
                                checked={formData.permissions.includes(perm)}
                                onCheckedChange={() => handlePermissionToggle(perm)}
                              />
                              <label
                                htmlFor={perm}
                                className="text-sm text-gray-700 cursor-pointer"
                              >
                                {perm.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="submit-team-member-button"
                type="submit"
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {editingMember ? 'Update Team Member' : 'Add Team Member'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamMembers.map((member, index) => (
          <div
            key={member.id}
            className="glass rounded-xl p-6 hover-lift transition-all"
            data-testid={`team-member-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  member.role === 'admin' 
                    ? 'bg-gradient-to-br from-purple-100 to-pink-100' 
                    : 'bg-gradient-to-br from-teal-100 to-cyan-100'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    member.role === 'admin' ? 'text-purple-600' : 'text-teal-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {member.first_name} {member.last_name}
                  </h3>
                  <span className={`badge ${
                    member.role === 'admin' ? 'badge-danger' : 'badge-info'
                  } text-xs`}>
                    {member.role === 'admin' ? 'Full Admin' : 'Sub Admin'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-sm text-gray-600">{member.email}</p>
              {member.phone && (
                <p className="text-sm text-gray-600">{member.phone}</p>
              )}
            </div>

            {member.role === 'sub_admin' && member.permissions && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Permissions ({member.permissions.length})</p>
                <div className="flex flex-wrap gap-1">
                  {member.permissions.slice(0, 3).map((perm) => (
                    <span key={perm} className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded">
                      {perm.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {member.permissions.length > 3 && (
                    <span className="text-xs text-gray-500">+{member.permissions.length - 3} more</span>
                  )}
                </div>
              </div>
            )}

            {member.id !== user?.id && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Button
                    data-testid={`edit-member-${index}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(member)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    data-testid={`delete-member-${index}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid={`reset-password-member-${index}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleSendPasswordReset(member)}
                    disabled={sendingReset === member.id}
                    className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                  >
                    {sendingReset === member.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 mr-1"></div>
                    ) : (
                      <KeyRound className="w-4 h-4 mr-1" />
                    )}
                    Send Reset
                  </Button>
                  <Button
                    data-testid={`change-password-member-${index}`}
                    size="sm"
                    variant="outline"
                    onClick={() => openChangePasswordDialog(member)}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    <Lock className="w-4 h-4 mr-1" />
                    Set Password
                  </Button>
                </div>
              </div>
            )}
            {member.id === user?.id && (
              <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
                This is you
              </div>
            )}
          </div>
        ))}

        {teamMembers.length === 0 && (
          <div className="col-span-full glass rounded-xl p-12 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No team members yet. Add your first team member!</p>
          </div>
        )}
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Lock className="w-5 h-5 mr-2 text-purple-600" />
              Set Password Manually
            </DialogTitle>
          </DialogHeader>
          
          {changingPasswordFor && (
            <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Setting new password for:
                </p>
                <p className="font-medium text-gray-900">
                  {changingPasswordFor.first_name} {changingPasswordFor.last_name}
                </p>
                <p className="text-sm text-gray-500">{changingPasswordFor.email}</p>
              </div>

              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="pr-10"
                    data-testid="new-password-input"
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> You'll need to share this password with the team member manually (in person, phone call, or secure message).
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setChangePasswordDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={changingPassword || !newPassword}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="confirm-change-password"
                >
                  {changingPassword ? 'Setting...' : 'Set Password'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
